import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import SectionTitle from '../ui/SectionTitle';
import GoldButton from '../ui/GoldButton';

interface State { name: string; email: string; message: string }
const empty: State = { name: '', email: '', message: '' };

export default function TimeCapsule() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<State>(empty);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [touched, setTouched] = useState<{ [k in keyof State]?: boolean }>({});

  const showError = (k: keyof State) => touched[k] && state[k].trim() === '';

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    if (!state.name.trim() || !state.email.trim() || !state.message.trim()) return;
    setStatus('sending');
    try {
      await fetch('https://formsubmit.co/ajax/loreandchris.capsule@gmail.com', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          ...state, 
          locale: i18n.language, 
          submittedAt: new Date().toISOString(),
          _subject: 'New Time Capsule Message',
          _captcha: false
        }),
      }).catch(() => null);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  return (
    <section id="capsule" className="bg-white" aria-labelledby="capsule-title">
      <div className="mx-auto max-w-2xl px-6 py-20 sm:py-28">
        <SectionTitle id="capsule-title">{t('timeCapsule.title')}</SectionTitle>
        <p className="mt-6 font-serif text-brand-ink/85 leading-relaxed text-center">
          {t('timeCapsule.description')}
        </p>

        {status === 'success' ? (
          <div className="mt-10 flex flex-col items-center text-center gap-3">
            <Lock className="text-brand-gold" size={28} aria-hidden="true" />
            <p className="font-serif italic text-brand-ink/85">{t('timeCapsule.success')}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-10 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <input
                  type="text"
                  placeholder={t('timeCapsule.namePlaceholder')}
                  value={state.name}
                  onChange={(e) => setState({ ...state, name: e.target.value })}
                  onBlur={() => setTouched((s) => ({ ...s, name: true }))}
                  aria-invalid={showError('name') || undefined}
                  className="w-full rounded-md border border-brand-gold/30 px-3 py-2 font-serif focus:outline-none focus:border-brand-gold"
                />
                {showError('name') && (
                  <p className="text-sm text-red-600 mt-1" role="alert">{t('timeCapsule.errorRequired')}</p>
                )}
              </label>
              <label className="block">
                <input
                  type="email"
                  placeholder={t('timeCapsule.emailPlaceholder')}
                  value={state.email}
                  onChange={(e) => setState({ ...state, email: e.target.value })}
                  onBlur={() => setTouched((s) => ({ ...s, email: true }))}
                  aria-invalid={showError('email') || undefined}
                  className="w-full rounded-md border border-brand-gold/30 px-3 py-2 font-serif focus:outline-none focus:border-brand-gold"
                />
                {showError('email') && (
                  <p className="text-sm text-red-600 mt-1" role="alert">{t('timeCapsule.errorRequired')}</p>
                )}
              </label>
            </div>
            <label className="block">
              <textarea
                rows={6}
                maxLength={500}
                placeholder={t('timeCapsule.messagePlaceholder')}
                value={state.message}
                onChange={(e) => setState({ ...state, message: e.target.value })}
                onBlur={() => setTouched((s) => ({ ...s, message: true }))}
                aria-invalid={showError('message') || undefined}
                className="w-full rounded-md border border-brand-gold/30 px-3 py-3 font-serif focus:outline-none focus:border-brand-gold resize-y"
              />
              <div className="flex items-center justify-between">
                {showError('message') ? (
                  <p className="text-sm text-red-600" role="alert">{t('timeCapsule.errorRequired')}</p>
                ) : (
                  <span />
                )}
                <span className="text-xs text-brand-ink/60 font-sans tabular-nums">
                  {state.message.length} / 500
                </span>
              </div>
            </label>

            {status === 'error' && (
              <p className="text-sm text-red-600 text-center" role="alert">
                {t('timeCapsule.errorBackend')}
              </p>
            )}

            <div className="pt-2 flex justify-center">
              <GoldButton type="submit" disabled={status === 'sending'}>
                {t('timeCapsule.submit')}
              </GoldButton>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
